// Propósito: leer variables CSS sin superar límites de complejidad.
export function readCssVariable(style: CSSStyleDeclaration, name: string): string {
  return style.getPropertyValue(name).trim();
}

export function getBodyStyle(): CSSStyleDeclaration {
  return getComputedStyle(document.body);
}

export function getFontVariables(style: CSSStyleDeclaration): Record<string, string> {
  return {
    bodyFontFamily: readCssVariable(style, '--bs-body-font-family'),
    bodyFontSize: readCssVariable(style, '--bs-body-font-size'),
    bodyFontWeight: readCssVariable(style, '--bs-body-font-weight'),
    bodyLineHeight: readCssVariable(style, '--bs-body-line-height')
  };
}

export function getThemeColors(style: CSSStyleDeclaration): Record<string, string> {
  return {
    theme: readCssVariable(style, '--bs-app-theme'),
    themeRgb: readCssVariable(style, '--bs-app-theme-rgb'),
    themeColor: readCssVariable(style, '--bs-app-theme-color'),
    themeColorRgb: readCssVariable(style, '--bs-app-theme-color-rgb')
  };
}

export function getDefaultColors(style: CSSStyleDeclaration): Record<string, string> {
  return {
    default: readCssVariable(style, '--bs-default'),
    defaultRgb: readCssVariable(style, '--bs-default-rgb')
  };
}

export function getPrimaryColors(style: CSSStyleDeclaration): Record<string, string> {
  return {
    primary: readCssVariable(style, '--bs-primary'),
    primaryRgb: readCssVariable(style, '--bs-primary-rgb'),
    primaryBgSubtle: readCssVariable(style, '--bs-primary-bg-subtle'),
    primaryText: readCssVariable(style, '--bs-primary-text'),
    primaryBorderSubtle: readCssVariable(style, '--bs-primary-border-subtle')
  };
}

export function getSecondaryColors(style: CSSStyleDeclaration): Record<string, string> {
  return {
    secondary: readCssVariable(style, '--bs-secondary'),
    secondaryRgb: readCssVariable(style, '--bs-secondary-rgb'),
    secondaryBgSubtle: readCssVariable(style, '--bs-secondary-bg-subtle'),
    secondaryText: readCssVariable(style, '--bs-secondary-text'),
    secondaryBorderSubtle: readCssVariable(style, '--bs-secondary-border-subtle')
  };
}

export function getSuccessColors(style: CSSStyleDeclaration): Record<string, string> {
  return {
    success: readCssVariable(style, '--bs-success'),
    successRgb: readCssVariable(style, '--bs-success-rgb'),
    successBgSubtle: readCssVariable(style, '--bs-success-bg-subtle'),
    successText: readCssVariable(style, '--bs-success-text'),
    successBorderSubtle: readCssVariable(style, '--bs-success-border-subtle')
  };
}

export function getWarningColors(style: CSSStyleDeclaration): Record<string, string> {
  return {
    warning: readCssVariable(style, '--bs-warning'),
    warningRgb: readCssVariable(style, '--bs-warning-rgb'),
    warningBgSubtle: readCssVariable(style, '--bs-warning-bg-subtle'),
    warningText: readCssVariable(style, '--bs-warning-text'),
    warningBorderSubtle: readCssVariable(style, '--bs-warning-border-subtle')
  };
}

export function getInfoColors(style: CSSStyleDeclaration): Record<string, string> {
  return {
    info: readCssVariable(style, '--bs-info'),
    infoRgb: readCssVariable(style, '--bs-info-rgb'),
    infoBgSubtle: readCssVariable(style, '--bs-info-bg-subtle'),
    infoText: readCssVariable(style, '--bs-info-text'),
    infoBorderSubtle: readCssVariable(style, '--bs-info-border-subtle')
  };
}

export function getDangerColors(style: CSSStyleDeclaration): Record<string, string> {
  return {
    danger: readCssVariable(style, '--bs-danger'),
    dangerRgb: readCssVariable(style, '--bs-danger-rgb'),
    dangerBgSubtle: readCssVariable(style, '--bs-danger-bg-subtle'),
    dangerText: readCssVariable(style, '--bs-danger-text'),
    dangerBorderSubtle: readCssVariable(style, '--bs-danger-border-subtle')
  };
}

export function getLightColors(style: CSSStyleDeclaration): Record<string, string> {
  return {
    light: readCssVariable(style, '--bs-light'),
    lightRgb: readCssVariable(style, '--bs-light-rgb'),
    lightBgSubtle: readCssVariable(style, '--bs-light-bg-subtle'),
    lightText: readCssVariable(style, '--bs-light-text'),
    lightBorderSubtle: readCssVariable(style, '--bs-light-border-subtle')
  };
}

export function getDarkColors(style: CSSStyleDeclaration): Record<string, string> {
  return {
    dark: readCssVariable(style, '--bs-dark'),
    darkRgb: readCssVariable(style, '--bs-dark-rgb'),
    darkBgSubtle: readCssVariable(style, '--bs-dark-bg-subtle'),
    darkText: readCssVariable(style, '--bs-dark-text'),
    darkBorderSubtle: readCssVariable(style, '--bs-dark-border-subtle')
  };
}

export function getMonoColors(style: CSSStyleDeclaration): Record<string, string> {
  return {
    inverse: readCssVariable(style, '--bs-inverse'),
    inverseRgb: readCssVariable(style, '--bs-inverse-rgb'),
    white: readCssVariable(style, '--bs-white'),
    whiteRgb: readCssVariable(style, '--bs-white-rgb'),
    black: readCssVariable(style, '--bs-black'),
    blackRgb: readCssVariable(style, '--bs-black-rgb')
  };
}

export function getCoolAccentColors(style: CSSStyleDeclaration): Record<string, string> {
  return {
    teal: readCssVariable(style, '--bs-teal'),
    tealRgb: readCssVariable(style, '--bs-teal-rgb'),
    lime: readCssVariable(style, '--bs-lime'),
    limeRgb: readCssVariable(style, '--bs-lime-rgb'),
    indigo: readCssVariable(style, '--bs-indigo'),
    indigoRgb: readCssVariable(style, '--bs-indigo-rgb'),
    purple: readCssVariable(style, '--bs-purple'),
    purpleRgb: readCssVariable(style, '--bs-purple-rgb')
  };
}

export function getWarmAccentColors(style: CSSStyleDeclaration): Record<string, string> {
  return {
    yellow: readCssVariable(style, '--bs-yellow'),
    yellowRgb: readCssVariable(style, '--bs-yellow-rgb'),
    pink: readCssVariable(style, '--bs-pink'),
    pinkRgb: readCssVariable(style, '--bs-pink-rgb'),
    cyan: readCssVariable(style, '--bs-cyan'),
    cyanRgb: readCssVariable(style, '--bs-cyan-rgb')
  };
}

export function getGrayScale(style: CSSStyleDeclaration): Record<string, string> {
  return {
    gray100: readCssVariable(style, '--bs-gray-100'), gray200: readCssVariable(style, '--bs-gray-200'),
    gray300: readCssVariable(style, '--bs-gray-300'), gray400: readCssVariable(style, '--bs-gray-400'),
    gray500: readCssVariable(style, '--bs-gray-500'), gray600: readCssVariable(style, '--bs-gray-600'),
    gray700: readCssVariable(style, '--bs-gray-700'), gray800: readCssVariable(style, '--bs-gray-800'),
    gray900: readCssVariable(style, '--bs-gray-900')
  };
}

export function getGrayRgbScale(style: CSSStyleDeclaration): Record<string, string> {
  return {
    gray100Rgb: readCssVariable(style, '--bs-gray-100-rgb'), gray200Rgb: readCssVariable(style, '--bs-gray-200-rgb'),
    gray300Rgb: readCssVariable(style, '--bs-gray-300-rgb'), gray400Rgb: readCssVariable(style, '--bs-gray-400-rgb'),
    gray500Rgb: readCssVariable(style, '--bs-gray-500-rgb'), gray600Rgb: readCssVariable(style, '--bs-gray-600-rgb'),
    gray700Rgb: readCssVariable(style, '--bs-gray-700-rgb'), gray800Rgb: readCssVariable(style, '--bs-gray-800-rgb'),
    gray900Rgb: readCssVariable(style, '--bs-gray-900-rgb')
  };
}

export function getSystemColors(style: CSSStyleDeclaration): Record<string, string> {
  return {
    muted: readCssVariable(style, '--bs-muted'), mutedRgb: readCssVariable(style, '--bs-muted-rgb'),
    emphasisColor: readCssVariable(style, '--bs-emphasis-color'), emphasisColorRgb: readCssVariable(style, '--bs-emphasis-color-rgb'),
    bodyBg: readCssVariable(style, '--bs-body-bg'), bodyBgRgb: readCssVariable(style, '--bs-body-bg-rgb'),
    bodyColor: readCssVariable(style, '--bs-body-color'), bodyColorRgb: readCssVariable(style, '--bs-body-color-rgb'),
    headingColor: readCssVariable(style, '--bs-heading-color')
  };
}

export function getSchemeColors(style: CSSStyleDeclaration): Record<string, string> {
  return {
    secondaryColor: readCssVariable(style, '--bs-secondary-color'), secondaryColorRgb: readCssVariable(style, '--bs-secondary-color-rgb'),
    secondaryBg: readCssVariable(style, '--bs-secondary-bg'), secondaryBgRgb: readCssVariable(style, '--bs-secondary-bg-rgb'),
    tertiaryColor: readCssVariable(style, '--bs-tertiary-color'), tertiaryColorRgb: readCssVariable(style, '--bs-tertiary-color-rgb'),
    tertiaryBg: readCssVariable(style, '--bs-tertiary-bg'), tertiaryBgRgb: readCssVariable(style, '--bs-tertiary-bg-rgb'),
    linkColor: readCssVariable(style, '--bs-link-color'), linkColorRgb: readCssVariable(style, '--bs-link-color-rgb'),
    linkHoverColor: readCssVariable(style, '--bs-link-hover-color'), linkHoverColorRgb: readCssVariable(style, '--bs-link-hover-color-rgb'),
    borderColor: readCssVariable(style, '--bs-border-color'), borderColorTranslucent: readCssVariable(style, '--bs-border-color-translucent')
  };
}

export function getBrandColors(style: CSSStyleDeclaration): Record<string, string> {
  return Object.assign({},
    getThemeColors(style),
    getDefaultColors(style),
    getPrimaryColors(style),
    getSecondaryColors(style),
    getSuccessColors(style),
    getWarningColors(style),
    getInfoColors(style),
    getDangerColors(style));
}

export function getNeutralColors(style: CSSStyleDeclaration): Record<string, string> {
  return Object.assign({},
    getLightColors(style),
    getDarkColors(style),
    getMonoColors(style),
    getCoolAccentColors(style),
    getWarmAccentColors(style),
    getGrayScale(style),
    getGrayRgbScale(style));
}

export function getLayoutColors(style: CSSStyleDeclaration): Record<string, string> {
  return Object.assign({},
    getSystemColors(style),
    getSchemeColors(style));
}

export function getColorVariables(style: CSSStyleDeclaration): Record<string, string> {
  return Object.assign({},
    getBrandColors(style),
    getNeutralColors(style),
    getLayoutColors(style));
}
